/** Revokes the object URL a fetch made, once it is there (a failed fetch made none). */
export const revokeObjectUrl = (url: Promise<string>) => void url.then((u) => URL.revokeObjectURL?.(u)).catch(() => {});

/** An image that could not be fetched (offline, turned off, gone on the server) is tried again after this. */
const RETRY_MS = 30_000;

interface Entry {
  url: Promise<string>;
  /** Views showing it. */
  users: number;
  failedAt: number | null;
}

/**
 * Images fetched with the session's credentials and shown as object URLs (profile photos, link
 * preview images). Images still shown are never revoked; of the others `idle` are kept for reuse,
 * least recently used first, and older ones are revoked. At most `maxFetches` requests run at
 * once, and a failed image is remembered for RETRY_MS instead of being asked for by every view.
 */
export class ObjectUrlCache {
  /** Least recently used first. */
  private readonly entries = new Map<string, Entry>();
  private readonly waiting: Array<() => void> = [];
  private fetching = 0;

  constructor(private readonly options: { idle: number; maxFetches: number; now: () => number }) {}

  /** Object URL of the image under `key`, fetched with `load` unless kept; call `release` once it is no longer shown. */
  get(key: string, load: () => Promise<Blob>): Promise<string> {
    let entry = this.entries.get(key);
    // Views still showing the failed attempt release this key later: they count for the new entry.
    let showing = 0;
    if (entry?.failedAt != null && this.options.now() - entry.failedAt >= RETRY_MS) {
      showing = entry.users;
      this.entries.delete(key);
      entry = undefined;
    }
    if (entry) {
      this.entries.delete(key); // set again below as the most recently used
    } else {
      const created: Entry = { url: this.fetch(load), users: showing, failedAt: null };
      created.url.catch(() => {
        created.failedAt = this.options.now();
      });
      entry = created;
    }
    entry.users += 1;
    this.entries.set(key, entry);
    return entry.url;
  }

  /** A view stopped showing it: it stays among the idle ones, and the least recently used beyond `idle` are revoked. */
  release(key: string) {
    const entry = this.entries.get(key);
    if (!entry?.users) return;
    entry.users -= 1;
    if (entry.users) return;
    this.entries.delete(key);
    this.entries.set(key, entry);
    let idle = 0;
    for (const e of this.entries.values()) if (!e.users) idle += 1;
    for (const [k, e] of this.entries) {
      if (idle <= this.options.idle) break;
      if (e.users) continue;
      this.entries.delete(k);
      revokeObjectUrl(e.url);
      idle -= 1;
    }
  }

  clear() {
    for (const entry of this.entries.values()) revokeObjectUrl(entry.url);
    this.entries.clear();
  }

  private async fetch(load: () => Promise<Blob>): Promise<string> {
    if (this.fetching < this.options.maxFetches) this.fetching += 1;
    else await new Promise<void>((resolve) => this.waiting.push(resolve));
    try {
      return URL.createObjectURL(await load());
    } finally {
      // The slot passes straight to the next waiting image.
      const next = this.waiting.shift();
      if (next) next();
      else this.fetching -= 1;
    }
  }
}
